/**
 * Shared token refresh singleton — prevents race conditions between
 * api.ts (401 handler) and auth.tsx (proactive/idle refresh) both
 * calling /api/auth/refresh simultaneously.
 *
 * If a refresh is already in progress, subsequent callers get the same
 * promise. This ensures only one refresh request is in flight at a time.
 */

import { API_BASE } from './api';
import { persistAuthStorage, clearAuthStorage, type AuthResponseData } from './authStorage';

let refreshPromise: Promise<AuthResponseData> | null = null;

/**
 * Perform a token refresh. If one is already in progress, returns the
 * same promise (deduplication). Uses the HttpOnly refresh-token cookie
 * (credentials: 'include'), not the access token.
 *
 * On success: persists new auth data to localStorage, returns it.
 * On 401: clears auth storage (genuinely expired refresh token).
 * On network error / 5xx: throws without clearing (retryable).
 */
export function singletonRefresh(): Promise<AuthResponseData> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        if (res.status === 401) {
          clearAuthStorage();
          throw new Error('Session expired');
        }
        // 5xx — backend temporarily unavailable, don't clear auth
        throw new Error(`Backend unavailable (${res.status})`);
      }

      const data: AuthResponseData = await res.json();
      persistAuthStorage(data);
      return data;
    } finally {
      // Keep the singleton for 3s after completion to prevent a race condition
      // where a second refresh request (e.g. from api.ts 401 handler) arrives
      // right after the first one completed and rotated the refresh token.
      // Without this delay, the second request would use the old (now-rotated)
      // token from the cookie and get 401 → clearAuthStorage → logout.
      setTimeout(() => { refreshPromise = null; }, 3000);
    }
  })();

  return refreshPromise;
}

/** Check if a refresh is currently in progress. */
export function isRefreshInProgress(): boolean {
  return refreshPromise !== null;
}
